import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { createPortalSession, getSubscriptionStatus } from '../../services/api';
import './SubscriptionStyles.css';

function SubscriptionManagement() {
  const navigate = useNavigate();
  const { user, refreshUser } = useUser();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const status = await getSubscriptionStatus();
      setSubscription(status);
    } catch (err) {
      console.error('Failed to load subscription status:', err);
      setError('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    setError('');
    try {
      const response = await createPortalSession();
      if (response.portal_url) {
        window.location.href = response.portal_url;
      } else {
        setError('Failed to open subscription portal');
      }
    } catch (err) {
      console.error('Portal error:', err);
      setError(err.response?.data?.error || 'Failed to open subscription portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'trialing':
        return 'status-trialing';
      case 'past_due':
        return 'status-warning';
      case 'canceled':
      case 'unpaid':
        return 'status-error';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="subscription-management">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading subscription details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-management">
      <div className="subscription-header">
        <h1>Subscription Management</h1>
        <p>Manage your subscription and billing</p>
      </div>

      {error && <div className="subscription-error">{error}</div>}

      <div className="subscription-card">
        <div className="subscription-info">
          <div className="info-row">
            <span className="label">Current Plan</span>
            <span className="value tier-name">{subscription?.tier_display || 'Free'}</span>
          </div>

          <div className="info-row">
            <span className="label">Status</span>
            <span className={`value status-badge ${getStatusBadgeClass(subscription?.status)}`}>
              {subscription?.status || 'No active subscription'}
            </span>
          </div>

          {subscription?.has_subscription && (
            <>
              <div className="info-row">
                <span className="label">Monthly Price</span>
                <span className="value">
                  ${((subscription?.monthly_price_cents || 0) / 100).toFixed(2)}/month
                </span>
              </div>

              <div className="info-row">
                <span className="label">Current Period Ends</span>
                <span className="value">{formatDate(subscription?.current_period_end)}</span>
              </div>

              {subscription?.cancel_at_period_end && (
                <div className="cancellation-warning-box">
                  <div className="warning-header">
                    <span className="warning-icon">&#9888;</span>
                    <h4>Subscription Ending</h4>
                  </div>
                  <p className="warning-date">
                    Your <strong>{subscription?.tier_display}</strong> subscription will end on{' '}
                    <strong>{formatDate(subscription?.current_period_end)}</strong>
                  </p>
                  <p className="warning-details">
                    After this date, you'll be downgraded to the Free tier and lose access to {subscription?.tier_display} features including higher usage limits.
                  </p>
                  <button
                    className="reactivate-button"
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                  >
                    {portalLoading ? 'Opening...' : 'Reactivate Subscription'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="subscription-actions">
          {subscription?.has_subscription ? (
            <button
              className="manage-button"
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              {portalLoading ? 'Opening Portal...' : 'Manage Subscription'}
            </button>
          ) : (
            <button className="upgrade-button" onClick={() => navigate('/pricing')}>
              View Plans
            </button>
          )}

          <button className="refresh-button" onClick={loadSubscriptionStatus}>
            Refresh Status
          </button>
        </div>
      </div>

      <div className="subscription-help">
        <h3>Need Help?</h3>
        <p>
          Use the Manage Subscription button to:
        </p>
        <ul>
          <li>Update your payment method</li>
          <li>Change your plan</li>
          <li>View billing history</li>
          <li>Cancel your subscription</li>
        </ul>
        <p>
          Want to upgrade?{' '}
          <button onClick={() => navigate('/pricing')} className="link-button">
            View all plans
          </button>
        </p>
      </div>
    </div>
  );
}

export default SubscriptionManagement;
